import React, { useContext } from 'react';
import classNames from 'classnames';
import { Link } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { useGetTranslations } from '@/generated/translations';
import { DrawerMegaMenuItem } from '@/types/drawerMegaMenuItem';
import styles from './DesktopSubMenu.module.scss';

interface DesktopSubMenuProps {
	item: DrawerMegaMenuItem;
	isActive?: boolean;
	closeDrawer?: () => void;
}

export const DesktopSubMenu: React.FunctionComponent<DesktopSubMenuProps> = ({ item, isActive, closeDrawer }) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);
	// Inactive panels stay mounted, and u-hide-visually only hides them visually, so they also
	// have to be hidden from assistive technology
	return (
		<li className={classNames(styles.wrapper, isActive ? '' : 'u-hide-visually')} aria-hidden={!isActive}>
			<h2 className={styles.level1Title}>{translations?.data.mobileMenu.menuHeadingPrefix + ' ' + item.name}</h2>

			{(item?.externalLink || item?.internalLink || item?.route?.externalRoute) && (
				<Link
					className={styles.seeAllLink}
					to={item?.internalLink || item?.route?.externalRoute}
					state={item?.route}
					linkUrl={item?.externalLink}
					onClick={closeDrawer}
					textLink
					tabIndex={!isActive ? -1 : undefined}
					// The accessible name must contain the visible "See all" text (WCAG 2.5.3 Label in Name)
					aria-label={translations?.data.mobileMenu.goToPage + ' ' + item.name}
				>
					{translations?.data.mobileMenu.goToPage}
				</Link>
			)}

			<ul className={styles.list}>
				{item.children?.map((itemLevel2: DrawerMegaMenuItem, index) => {
					return (
						<li className={styles.item} key={`${index}-${itemLevel2.name}`}>
							{itemLevel2?.externalLink ||
							itemLevel2?.internalLink ||
							itemLevel2?.route?.externalRoute ? (
								<Link
									className={styles.level2Title}
									to={itemLevel2?.internalLink || itemLevel2?.route?.externalRoute}
									state={itemLevel2?.route}
									linkUrl={itemLevel2?.externalLink}
									onClick={closeDrawer}
									textLink
									tabIndex={!isActive ? -1 : undefined}
								>
									{itemLevel2.name}
								</Link>
							) : (
								<span className={styles.noLink}>{itemLevel2.name}</span>
							)}

							<ul className={styles.subList}>
								{itemLevel2.children?.map((itemLevel3, subIndex) => {
									return (
										<li className={styles.item} key={`${subIndex}-${itemLevel3.name}`}>
											<Link
												className={styles.link}
												to={itemLevel3?.internalLink || itemLevel3?.route?.externalRoute}
												state={itemLevel3?.route}
												linkUrl={itemLevel3?.externalLink}
												onClick={closeDrawer}
												textLink
												tabIndex={!isActive ? -1 : undefined}
											>
												{itemLevel3.name}
											</Link>
										</li>
									);
								})}
							</ul>
						</li>
					);
				})}
			</ul>
		</li>
	);
};
